import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  CompleteExpressQueryKiosk_company_categories,
  CompleteExpressQueryKiosk_company_categories_items,
} from '../../Components/useExpressQuery/__queries__';
import Navbar from '../../Components/Navbar';
import CategoryPage from '../Category';
import ItemPage from '../Item';
import EditItemPage from '../Item/EditItem';
import Cart from '../Cart';

import Dialog from '../../Components/Dialog';
import { LocalItem, LocalVariation } from '~Types';
import _ from 'lodash';
import { generateID } from '~reducers';
import { useI18n, useLogger, useMetadata, useSelector } from '~Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import { useApolloClient } from '@apollo/client';
import { SERVICE_TYPE } from '~../__queries__';
import { getVatRate } from '../../utils/vat';
import { companiesWithTakeAwayBag } from '~Scenes/SelfServiceKiosks';
import { packageItems } from '~Utils/packagedItems';

const Wrapper = styled.div`
  top: 0;
  left: 0;
  box-sizing: content-box;
  width: 100vw;
  animation: fade-in 0.3s ease-in-out;

  ::-webkit-scrollbar {
    display: none;
  }
`;

export enum SCENES {
  DETAILS = 'DETAILS',
  UPSELLS = 'UPSELLS',
}

export const maxItemsQuantity = 50;

const EDIT_ANIMATION_DURATION = 300;

interface Props {
  categories: CompleteExpressQueryKiosk_company_categories[];
  orderId: string | null;
  venueId: string;
  items: LocalItem[];
  setItems: React.Dispatch<React.SetStateAction<LocalItem[]>>;
  checkLegalAgeAtCheckout: (legalCheck: boolean) => void;
  reset: () => void;
  serviceType: SERVICE_TYPE | null;
}

const General: React.FunctionComponent<Props> = ({
  categories,
  orderId,
  venueId,
  items,
  setItems,
  checkLegalAgeAtCheckout,
  reset,
  serviceType,
}: Props) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [dialogActiveScreen, setDialogActiveScreen] = useState<string>('');
  const [itemScene, setItemScene] = useState<SCENES>(SCENES.DETAILS);
  const [editItem, setEditItem] = useState<LocalItem | null>(null);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const companyId = useSelector((state) => state.company);

  const metadata = useMetadata();
  const logger = useLogger('epos');
  const { i18n } = useI18n();

  const allItems: CompleteExpressQueryKiosk_company_categories_items[] = useMemo(() => {
    const _items = _.flatMap(categories, 'items');
    const upsellItems = _.flatMap(_.flatMap(_items, 'upsales'), 'items').filter(Boolean);
    return _.uniqBy([..._items, ...upsellItems], 'id');
  }, [categories]);

  const categoriesWithUkVatRate = useMemo(() => {
    return categories.map((c) => ({
      id: c.id,
      ukVatRate: c.ukVatRate,
    }));
  }, [categories]);

  const selectedItem = useMemo(() => allItems.find(({ id }) => id === activeItemId), [
    allItems,
    activeItemId,
  ]);

  const itemCategoryMap = useMemo(() => {
    return categories.reduce(
      (obj, category) => {
        category.items.forEach((item) => {
          obj[item.id] = {
            id: category.id,
            name: i18n(category.nameLang),
          };
        });
        return obj;
      },
      {} as Record<
        string,
        {
          id: string;
          name: string;
        }
      >,
    );
  }, [categories]);

  const itemsForPackaging = useMemo(
    () =>
      allItems.map((item) => ({
        id: item.id,
        itemId: item.id,
        title: i18n(item.titleLang),
        description: i18n(item.descriptionLang),
        variations: [],
        upsales: item.upsales,
        upsalesSortOrder: item.upsalesSortOrder,
        removedVariations: [],
        vatRate: item.vatRate,
        imageUrl: item.image?.file.url ?? '',
        ageRestricted: item.ageRestricted,
        modificationGroups: item.modificationGroups ?? [],
        allergyTags: item.allergyTags,
      })),
    [allItems],
  );

  const editItemInOrder = useCallback(
    (id: string, variations: LocalVariation[], removedVariations: LocalVariation[]) => {
      setItems((state) =>
        state.map((item) => (item.id === id ? { ...item, variations, removedVariations } : item)),
      );

      handleCloseDialog();
    },
    [dialogActiveScreen],
  );

  const addTakeAwayBag = () => {
    const companyWithTakeAwayBag = companiesWithTakeAwayBag.find((c) => c.companyId === companyId);

    if (!companyWithTakeAwayBag) {
      return;
    }

    const alreadyInCart = items.find((i) => i.itemId === companyWithTakeAwayBag.itemId);

    if (alreadyInCart) {
      return;
    }

    const item = itemsForPackaging.find((i) => i.id === companyWithTakeAwayBag.itemId);

    if (!item) {
      return;
    }

    const itemCartId = generateID();
    const categoryId = itemCategoryMap[item.itemId].id;
    const vatRate = getVatRate({
      categoryId: categoryId,
      categoriesWithUkVatRate,
      itemVatRate: item.vatRate,
      serviceType,
    });

    setItems((state) => [
      ...state,
      {
        itemId: item.itemId,
        title: item.title,
        variations: [],
        count: 1,
        categoryId,
        removedVariations: [],
        id: itemCartId,
        parentId: undefined,
        fraction: 1,
        vatRate,
        description: item.description,
        ageRestricted: false,
        modificationGroups: [],
        allergyTags: [],
      },
    ]);
  };

  const addItemToOrder = useCallback(
    (
      id: string,
      variations: LocalVariation[],
      removedVariations: LocalVariation[],
      count: number,
      hasUpsales?: boolean,
    ): { cartItemIds: string[] } => {
      const item = itemsForPackaging.find((i) => i.id === id)!;
      const cartItemIds: string[] = [];
      const categoryId = itemCategoryMap[item.itemId].id;
      const vatRate = getVatRate({
        categoryId,
        categoriesWithUkVatRate,
        itemVatRate: item.vatRate,
        serviceType,
      });

      _.times(count, () => {
        const itemCartId = generateID();
        cartItemIds.push(itemCartId);

        setItems((state) => [
          ...state,
          {
            itemId: id,
            title: item.title,
            variations: variations.filter((vari) => {
              return (
                !vari.name.startsWith('Inget ') &&
                !vari.name.startsWith('Ingen ') &&
                !vari.name.startsWith('Inga ') &&
                !vari.name.startsWith('No ')
              );
            }),
            count: 1,
            categoryId,
            removedVariations,
            id: itemCartId,
            parentId: hasUpsales ? itemCartId : undefined,
            fraction: 1,
            vatRate,
            description: item.description,
            ageRestricted: item.ageRestricted,
            modificationGroups: item.modificationGroups ?? [],
          },
        ]);
      });

      // Add a takeaway bag if it is TAKE AWAY and there is a corresponding company
      if (serviceType === 'TAKE_AWAY') {
        addTakeAwayBag();
      }

      if (!hasUpsales) {
        handleCloseDialog();
      }

      return { cartItemIds };
    },
    [items, itemsForPackaging],
  );

  const addItemUpsellToOrder = useCallback((upsellItemId: string, parentCartId: string) => {
    const item = itemsForPackaging.find((i) => i.id === upsellItemId)!;
    const categoryId = itemCategoryMap[upsellItemId].id;
    const vatRate = getVatRate({
      categoryId,
      categoriesWithUkVatRate,
      itemVatRate: item.vatRate,
      serviceType,
    });

    if (!item) {
      logger.error('Item was undefined when adding upsell.', {
        upsellItemId,
        parentCartId,
        item,
      });
    }

    const itemCartId = generateID();
    setItems((state) => [
      ...state,
      {
        itemId: upsellItemId,
        title: item.title,
        variations: [],
        count: 1,
        removedVariations: [],
        categoryId,
        id: itemCartId,
        parentId: parentCartId,
        fraction: 1,
        vatRate,
        description: item.description,
        ageRestricted: item.ageRestricted,
      },
    ]);

    handleCloseDialog();
  }, []);

  useEffect(() => {
    const filteredCategories = categories.filter((c) => !c.hideInCustomerOrderingFlows);
    const id = filteredCategories[0]?.id;
    if (id) {
      setSelectedCategoryId(id);
    }
  }, []);

  const handleCloseDialog = () => {
    if (dialogActiveScreen === 'EDIT_ITEM') {
      setShowEdit(false);

      setTimeout(() => {
        setDialogActiveScreen('CART');
        setActiveItemId(null);
        setEditItem(null);
      }, EDIT_ANIMATION_DURATION);
    } else {
      setActiveItemId(null);
      setDialogActiveScreen('');
    }
  };

  const handleClickItem = (id: string, instant?: boolean) => {
    const cartItemsQuantity = items.length;
    if (instant) {
      if (cartItemsQuantity < maxItemsQuantity) {
        addItemToOrder(id, [], [], 1);
      }
      return;
    }

    setTimeout(() => {
      setActiveItemId(id);
      setDialogActiveScreen('ITEM');
    }, 350);
  };

  const handleClickEditItem = (item: LocalItem) => {
    setDialogActiveScreen('EDIT_ITEM');
    setShowEdit(true);
    setEditItem(item);
    setActiveItemId(item.itemId);
  };

  const dialogVisible = !!dialogActiveScreen;

  const findEntryFromItem = useEntryFinder();
  const client = useApolloClient();

  const getPackagedItem = useCallback((id: string) => {
    const orderItem = itemsForPackaging.find((i) => i.id === id)!;
    const packaged = packageItems(
      [...items, orderItem] as LocalItem[],
      venueId,
      null,
      null,
      findEntryFromItem,
      client,
      i18n,
      metadata.data.discountExcludedCategoryIds,
    );
    const item = packaged.find((i) => i.id === id)!;
    return item;
  }, []);

  return (
    <Wrapper id="express">
      <Navbar serviceType={serviceType} openWelcomeScreen={reset} />
      <CategoryPage
        orderId={orderId}
        venueId={venueId}
        items={items}
        selectedCategoryId={selectedCategoryId}
        categories={categories}
        onClickItem={handleClickItem}
        onChangeCategory={setSelectedCategoryId}
        onSetDialogActiveScreen={(screen) => setDialogActiveScreen(screen)}
      />
      <Dialog
        visible={dialogVisible}
        canClose={itemScene !== 'UPSELLS'}
        onClose={handleCloseDialog}
        initialCloseText={dialogActiveScreen === 'CART' ? i18n('SSK.KeepShopping') : null}>
        {dialogActiveScreen === 'ITEM' && selectedItem && (
          <ItemPage
            cartItems={items}
            cartItemsQuantity={items.length}
            item={getPackagedItem(selectedItem.id)}
            onAddToOrder={addItemToOrder}
            onAddItemUpsellToOrder={addItemUpsellToOrder}
            onClose={handleCloseDialog}
            itemScene={itemScene}
            setItemScene={setItemScene}
          />
        )}

        {(dialogActiveScreen === 'CART' || dialogActiveScreen === 'EDIT_ITEM') && (
          <Cart
            checkLegalAgeAtCheckout={checkLegalAgeAtCheckout}
            orderId={orderId}
            venueId={venueId}
            items={items}
            setItems={setItems}
            onClose={handleCloseDialog}
            onClickItem={handleClickEditItem}
          />
        )}
        <EditItemPage
          cartItemsQuantity={items.length}
          showEdit={showEdit}
          selectedItem={selectedItem}
          dialogActiveScreen={dialogActiveScreen}
          item={selectedItem ? getPackagedItem(selectedItem.id) : undefined}
          onEditOrder={editItemInOrder}
          editItem={editItem}
          animationDuration={EDIT_ANIMATION_DURATION}
        />
      </Dialog>
    </Wrapper>
  );
};

export default General;
